<template>
    <article class="product-wrapper">
        <div
            class="product-card"
            :class="{
                'product-card--no-border': hideCard,
                'product-card--no-thumbnails': !hasOutfitContext,
            }"
        >
            <section class="interaction-buttons-container">
                <swap-item-button @click="setSwapProduct" iconSize="25px" />
                <slot name="interaction-buttons" />
            </section>

            <section class="current-product-image-container">
                <slide-show
                    :images="slideShowImages"
                    :navigateTo="0"
                    @image-changed="imageChanged"
                    @image-clicked="imageClicked"
                />
            </section>

            <div class="current-product-hearth-button">
                <save-product
                    v-if="!isProductOOS"
                    :product="currentProduct"
                    :outfitId="outfit && outfit.outfit_id"
                />
            </div>

            <section class="current-product-container">
                <div class="current-product-title-container">
                    <h3 class="current-product-title">
                        {{ currentProduct.product_title }}
                    </h3>
                    <p
                        v-line-clamp="descriptionLineClamp"
                        class="current-product-description"
                        @click="toggleDescription"
                    >
                        {{ currentProduct.product_description }}
                    </p>
                    <a
                        href="#"
                        class="view-on-brand"
                        @click="
                            openExternalURL(
                                currentProduct.product_buy_url,
                                $event
                            )
                        "
                        >View product via brand</a
                    >
                </div>
                <h3 class="current-product-price">
                    {{ currentProduct.product_price | currency }}
                </h3>
            </section>

            <section
                class="outfit-products-container"
                v-if="hasOutfitContext"
                :class="{
                    'outfit-products-container--five-products':
                        hasOutfitContext && outfit.products.length > 4,
                }"
            >
                <div
                    class="outfit-product"
                    :class="{
                        'outfit-product--current':
                            currentProduct.product_id === product.product_id,
                    }"
                    v-for="product in outfit.products"
                    :key="product.product_id"
                    @click="() => outfitImageClicked(product.product_id)"
                >
                    <img
                        class="outfit-product-image"
                        :src="product.product_image_url"
                        :alt="product.product_title"
                    />
                </div>
            </section>
        </div>

        <section class="current-product-options-container">
            <color-size-selector
                ref="colorSizeSelector"
                :colors="currentProduct.product_colors"
                :sizes="sortedSizes"
                :selectedColorId="currentProduct.product_option_value_id_color"
                :selectedSizeId="currentProduct.product_option_value_id_size"
                :isPristine="currentProduct.isPristine"
                @color-changed="
                    (event) =>
                        productChanged(
                            setOption('color', currentProduct, event)
                        )
                "
                @size-changed="
                    (event) =>
                        productChanged(setOption('size', currentProduct, event))
                "
            />
        </section>

        <section class="footer-buttons-container">
            <share-outfit v-if="hasOutfitContext" :outfit="outfit" />
            <share-product
                v-else
                :id="currentProduct.product_id"
                :url="slideShowImages[0] && slideShowImages[0].uri"
            />
            <template v-if="isVariantOOS || isProductOOS">
                <swap-item-button
                    v-if="hasOutfitContext"
                    classes="footer-button footer-button--pink btn-16"
                    text="SWAP"
                    icon="white"
                    @click="() => setSwapProduct(true)"
                />
                <button
                    class="footer-button footer-button--dark-gray btn-16 pd-30"
                    :disabled="true"
                >
                    OUT OF STOCK
                </button>
            </template>
            <template v-else>
                <purchase-outfit-button
                    v-if="hasOutfitContext"
                    class="footer-button"
                    :outfit="outfit"
                    @click.stop="purchaseOutfit"
                />
                <save-product
                    v-if="showFooterSaveButton"
                    saveProductClassList="footer-button"
                    child="save-button-footer"
                    :product="currentProduct"
                    :outfitId="outfit && outfit.outfit_id"
                />
                <button
                    class="footer-button footer-button--pink"
                    :class="{
                        'footer-button--only':
                            !hasOutfitContext && !showFooterSaveButton,
                    }"
                    @click="addProductToCart"
                >
                    Add item to Cart
                </button>
            </template>
        </section>
        <transition name="fade">
            <swap-item
                v-if="showSwapProduct"
                :product="currentProduct"
                :showSizes="showSwapProductSizes"
                :swapCb="hasOutfitContext ? '' : swapProduct"
                @close="() => (this.showSwapProduct = false)"
            />
        </transition>
    </article>
</template>

<script>
import SlideShow from "../common/slideshow-v2.vue";
import SaveProduct from "./save-product.vue";
import ColorSizeSelector from "./color-size-selector.vue";
import SwapItemButton from "./swap-item-button.vue";
import SwapItem from "./swap-item.vue";
import ShareOutfit from "../outfit/share-outfit.vue";
import ShareProduct from "./share-product.vue";
import PurchaseOutfitButton from "../outfit/purchase-outfit-button.vue";

import Mixins from "../../mixins/mixins.js";
import ProductMixins from "../../mixins/product-mixins.js";

import { eventNames } from "../../store/utils/tracking";

export default {
    props: {
        currentProduct: Object,
        outfit: Object,
        hideCard: {
            type: Boolean,
            default: true,
        },
        slideShowImages: Array,
        imageChanged: Function,
        imageClicked: Function,
        outfitImageClicked: Function,
        productChanged: Function,
        showFooterSaveButton: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        SlideShow,
        SaveProduct,
        ColorSizeSelector,
        SwapItemButton,
        SwapItem,
        ShareOutfit,
        PurchaseOutfitButton,
        ShareProduct,
    },
    mixins: [Mixins, ProductMixins],
    data() {
        return {
            isVariantOOS: false,
            showSwapProduct: false,
            showSwapProductSizes: false,
            descriptionLineClamp: 2,
        };
    },
    computed: {
        sortedSizes() {
            return this.sortSizes(this.getColorSizes(this.currentProduct));
        },
        hasOutfitContext() {
            return !!this.outfit && !!this.outfit.products;
        },
        isProductOOS() {
            return (
                this.currentProduct.product_colors.every(
                    (color) => color.available_sizes.length === 0
                ) &&
                this.currentProduct.product_sizes.every(
                    (size) => size.in_stock == 0
                )
            );
        },
    },
    methods: {
        addProductToCart() {
            if (this.$refs.colorSizeSelector?.isValid) {
                const product = this.currentProduct;

                this.$helpers.trackEvent(eventNames.ADD_TO_CART, {
                    content_id: product.product_id,
                    name: product.product_name,
                    price: product.product_price,
                });
                this.$store
                    .dispatch({
                        type: "cart/insertProductsToCart",
                        products: [
                            {
                                ...product,
                                outfit_id: this.outfitId,
                                selected: true,
                            },
                        ],
                    })
                    .then(() => {
                        this.$router.push({ path: "/cart" });
                        this.$store.commit({
                            type: "search/setSearchOpen",
                            searchOpen: false,
                        });
                    })
                    .catch((error) => {
                        this.$helpers.trackEvent("add_to_cart_error", {
                            content_id: product.product_id,
                        });
                        this.$store.dispatch(
                            "errors/put",
                            error.message ||
                                "Invalid product or option combination."
                        );
                    });
            } else {
                this.updatePristineStatus("color", this.currentProduct);
                this.updatePristineStatus("size", this.currentProduct);
            }
        },
        purchaseOutfit() {
            this.$store.commit("outfits/setCurrentOutfit", {
                outfit: this.outfit,
            });
        },
        setSwapProduct(showSizes) {
            if (!!this.outfit?.products) {
                this.$store.commit("outfits/setCurrentOutfit", {
                    outfit: this.outfit,
                });
            }
            this.showSwapProductSizes = showSizes;
            this.showSwapProduct = true;

            this.$helpers.trackEvent("swap_init", {});
        },
        toggleDescription() {
            switch (this.descriptionLineClamp) {
                case 2:
                    return (this.descriptionLineClamp = 0);
                case 0:
                    return (this.descriptionLineClamp = 2);
            }
        },
        trackProductViewEvent() {
            this.$helpers.trackEvent(eventNames.VIEW_PRODUCT, {
                content_id: this.currentProduct.product_id,
                price: this.currentProduct.product_price,
            });
        },
        swapProduct(newProduct) {
            this.$emit("change-product", newProduct);
            return new Promise((resolve) => resolve(newProduct));
        },
        openExternalURL(url, event) {
            event.preventDefault();
            cordova.InAppBrowser.open(url, "_blank", "location=yes");
            return false;
        },
    },
    created() {
        if (typeof window.Keyboard.setResizeMode !== "undefined") {
            window.Keyboard.setResizeMode("body");
        }
    },
    mounted() {
        //Set OOS status from Color-Size component
        this.trackProductViewEvent();
        if (typeof this.$refs.colorSizeSelector !== "undefined") {
            this.isVariantOOS = this.$refs.colorSizeSelector?.isSizeOOS;
            this.$watch(
                () => this.$refs.colorSizeSelector?.isSizeOOS,
                (isOOS) => {
                    this.isVariantOOS = isOOS;
                }
            );
        }
    },
    beforeDestroy() {
        this.$store.commit({
            type: "footer/showFooter",
        });
        if (typeof window.Keyboard.setResizeMode !== "undefined") {
            window.Keyboard.setResizeMode("native");
        }
    },
};
</script>

<style lang="scss" scoped>
.product-wrapper {
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;

    .product-card {
        border: 1px solid #c6c6c6;
        border-radius: 28px;
        box-shadow: 2px 2px 9px 0 rgba(0, 0, 0, 0.04);
        display: grid;
        grid-template-rows: 1fr 20px auto 61px;
        grid-template-columns: 20px 1fr 20px;
        flex: 1;
        min-height: 0;
        grid-gap: 3vh 1px;
        padding: 5vw 4vw;

        &--no-border {
            box-shadow: none;
            border: none;
            padding: 0;
            & + .current-product-options-container {
                margin-left: 0;
                margin-right: 0;
            }
        }
        &--no-thumbnails {
            grid-template-rows: 1fr 20px auto;
        }
    }

    .interaction-buttons-container {
        grid-area: 1 / 3 / 2 / 4;
        .swap-button {
            .swap-icon {
                width: 100%;
            }
        }
    }

    .current-product-image-container {
        grid-area: 1 / 2 / 3 / 3;
        display: flex;
        position: relative;
    }

    .current-product-hearth-button {
        grid-area: 2 / 1 / 3 / 2;
        position: relative;
    }

    .current-product-container {
        grid-area: 3 / 1 / 4 / 4;
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        h3,
        h5 {
            font-size: 14px;
            line-height: 12px;
            font-weight: 700;
            font-family: "Lato";
        }
        .current-product-title-container {
            margin-right: 3%;
            .current-product-description {
                margin-top: 9px;
                color: #757575;
                font-size: 12px;
                font-weight: 400;
                word-break: break-word;
            }

            a {
                font-size: 12px;
            }
        }

        .view-on-brand {
            color: #000;
            font-weight: bold;
        }
    }
    .outfit-products-container {
        grid-area: 4 / 1 / 5 / 4;
        display: flex;
        justify-content: space-around;
        &--five-products {
            .outfit-product {
                @media screen and (max-width: 340px) {
                    margin: 0 1px;
                    height: 56px;
                }
            }
        }
        .outfit-product {
            border-radius: 50%;
            border: 1px solid #ececec;
            overflow: hidden;
            display: flex;
            justify-content: center;
            flex: 61px 0 1;
            padding: 0px 4.5px;

            &--current {
                border: 1.2px solid #f2c0de;
            }
            .outfit-product-image {
                object-fit: contain;
                width: 100%;
                height: 100%;
            }
        }
    }
    .current-product-options-container {
        margin: 15px 0;
    }

    .footer-buttons-container {
        display: flex;
        justify-content: space-evenly;
        margin-bottom: 13px;
        height: auto;
        .footer-button {
            border-radius: 23px;
            padding: 0px 13px;
            height: 46px;
            font-family: "Lato";
            font-size: 14px;
            background-color: #efefef;
            flex: 0.45;
            &--pink {
                background-color: #f2c0de;
                color: white;
            }
            &--dark-gray {
                background-color: #95989a;
                color: white;
            }
            &--icon {
                border-radius: 50%;
            }
            &--only {
                flex: 0.95;
            }
            &.btn-16 {
                &,
                span {
                    font-size: 16px;
                }
            }
            &.pd-30 {
                @media screen and (min-width: 341px) {
                    padding: 0 30px;
                }
            }
        }
    }
}
</style>
